import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';
/** Local midnight, as an instant — "today" is the clinic's day, not UTC's. */
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString(); };

/**
 * FLOWS — a multi-step clinical sequence RUNNING for one patient (backlog §6).
 *
 * A protocol is a document; a flow is an instance of work: "Dilated fundus
 * exam — drops (nurse) → wait 20 min → OCT (technician) → back to the doctor".
 * It runs BESIDE a visit, not inside it, which is why Today lists it as
 * unfinished and the encounter carries it as its own block.
 *
 * Templates live in Library ▸ Flows; instances are started from a visit.
 */

export type FlowRole = 'doctor' | 'nurse' | 'technician' | 'reception';

export interface FlowStepDef {
  label: string;
  assigneeRole: FlowRole;
  /** A timed step ("dilating — 20 min") returns to the next assignee at 0. */
  durationMin?: number | null;
  /** The Item this step was picked from, when it was not written as free text. */
  itemId?: string | null;
  /** The step's own note — "protocol: Tropicamide + Phenylephrine". */
  note?: string | null;
  /**
   * THE AVAILABILITY FORK, per branch. A step this branch cannot perform
   * resolves to EXTERNAL — a dispatched request, the episode PENDING, the
   * return in reception's book-list. 'skip' drops the step; null waits for us.
   */
  ifUnavailable?: 'external' | 'skip' | null;
}

export interface FlowStep extends FlowStepDef {
  state: 'pending' | 'active' | 'waiting' | 'done' | 'skipped';
  startedAt?: string | null;
  /** When a timed step runs out. */
  readyAt?: string | null;
  doneAt?: string | null;
  doneByName?: string | null;
}

export type BookingType = 'routine' | 'follow-up' | 'urgent' | 'emergency';

export interface FlowTemplate {
  id: string;
  name: string;
  description?: string | null;
  steps: FlowStepDef[];
  /** Branches the flow runs at; null / empty = every branch. */
  branchIds?: string[] | null;
  /** null = the encounter's Flow button; else every booking of that type starts it. */
  triggerBookingType?: BookingType | null;
  isActive: boolean;
  createdById?: string | null;
  createdByName?: string | null;
  usageCount: number;
  updatedAt: string;
}

export interface FlowInstance {
  id: string;
  templateId?: string | null;
  name: string;
  patientId?: string | null;
  patientName?: string | null;
  medicalRecordId?: string | null;
  appointmentId?: string | null;
  state: 'running' | 'done' | 'cancelled';
  currentStep: number;
  steps: FlowStep[];
  startedAt: string;
  startedByName?: string | null;
  completedAt?: string | null;
}

const unwrap = <T,>(res: { data?: unknown }): T => {
  const d = res.data as { data?: unknown } | undefined;
  return ((d && typeof d === 'object' && 'data' in d ? d.data : d) ?? null) as T;
};

export const flowsApi = {
  async templates(): Promise<FlowTemplate[]> {
    return unwrap<FlowTemplate[]>(await apiClient.get('/flows/templates')) ?? [];
  },
  async createTemplate(body: { name: string; description?: string; steps: FlowStepDef[]; branchIds?: string[]; triggerBookingType?: BookingType | null }) {
    return unwrap<FlowTemplate>(await apiClient.post('/flows/templates', body));
  },
  async updateTemplate(id: string, body: Partial<{ name: string; description: string; steps: FlowStepDef[]; branchIds: string[]; triggerBookingType: BookingType | null; isActive: boolean }>) {
    return unwrap<FlowTemplate>(await apiClient.patch(`/flows/templates/${id}`, body));
  },
  async instances(params: { since?: string; patientId?: string; medicalRecordId?: string; state?: string }) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
    return unwrap<FlowInstance[]>(await apiClient.get(`/flows/instances?${q.toString()}`)) ?? [];
  },
  /**
   * Start a Library flow (`templateId`), or a SINGLE TASK written on the spot
   * (`name` + one step) — the encounter's "Single task ▸ · Custom". A one-off
   * leaves nothing behind in the Library but runs, advances and is chased
   * exactly like any other flow.
   */
  async start(body: {
    patientId: string; medicalRecordId?: string; appointmentId?: string;
    templateId?: string; name?: string; steps?: FlowStepDef[];
  }) {
    return unwrap<FlowInstance>(await apiClient.post('/flows/instances', body));
  },
  async advance(id: string) {
    return unwrap<FlowInstance>(await apiClient.post(`/flows/instances/${id}/advance`, {}));
  },
  async cancel(id: string) {
    return unwrap<FlowInstance>(await apiClient.post(`/flows/instances/${id}/cancel`, {}));
  },
};

/** Flows running today — Today's Unfinished rows. */
export function useFlowsToday() {
  const q = useQuery({
    queryKey: ['flows', 'instances', 'running'],
    /* A RUNNING flow is unfinished whatever day it started — yesterday's
       forgotten one is exactly the one Today must not drop. */
    queryFn: () => flowsApi.instances({ state: 'running' }),
    staleTime: 20_000,
    refetchInterval: 60_000,
    retry: false,
  });
  return { ...q, data: q.data ?? [] };
}

/** Flows attached to one visit — the encounter's Flow block. */
export function useVisitFlows(medicalRecordId?: string | null, patientId?: string | null) {
  const q = useQuery({
    queryKey: ['flows', 'instances', 'visit', medicalRecordId ?? null, patientId ?? null],
    queryFn: () => flowsApi.instances(medicalRecordId ? { medicalRecordId } : { patientId: patientId ?? undefined, since: startOfToday() }),
    enabled: Boolean(medicalRecordId || patientId),
    staleTime: 20_000,
    retry: false,
  });
  return { ...q, data: q.data ?? [] };
}

export function useFlowTemplates() {
  const q = useQuery({
    queryKey: ['flows', 'templates'],
    queryFn: () => flowsApi.templates(),
    staleTime: 60_000,
    retry: false,
  });
  return { ...q, data: q.data ?? [] };
}

export function useFlowMutations() {
  const qc = useQueryClient();
  const done = () => void qc.invalidateQueries({ queryKey: ['flows'] });
  return {
    start: useMutation({ mutationFn: flowsApi.start, onSuccess: done }),
    advance: useMutation({ mutationFn: flowsApi.advance, onSuccess: done }),
    cancel: useMutation({ mutationFn: flowsApi.cancel, onSuccess: done }),
  };
}

/** mm:ss left on a timed step, ticking. `null` when the step is not timed. */
export function useCountdown(readyAt?: string | null): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!readyAt) return undefined;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [readyAt]);
  if (!readyAt) return null;
  const left = Math.max(0, Math.round((new Date(readyAt).getTime() - now) / 1000));
  return `${String(Math.floor(left / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
}

/** "08:12" — the wall-clock time a flow started. */
export function clock(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
